import { normalizeOrgId } from './instance'

/**
 * Native B1 (NB1-11): a typed code carries its server. Organizer surfaces show codes as
 * `<orgId>-<CODE>`; server codes are always exactly 8 alphanumerics (`GrantShortCode.NewCode`),
 * so the split is unambiguous: the last 8 alphanumerics are the code, anything before them is the
 * org ID. The server never sees the prefix (it normalizes the bare code itself).
 */
const CODE_LENGTH = 8

/**
 * `{ orgId, code }` — `code` is the bare code, upper-cased with separators stripped.
 * - exactly 8 alphanumerics → a bare code, `orgId` null;
 * - more than 8 → the leading part (separators between it and the code dropped) must be a valid
 *   org ID (NB1-9). If it is not, the whole input is INVALID: `{ orgId: null, code: '' }`.
 * - fewer than 8 → passed through bare (`orgId` null) so the server answers it as a wrong code.
 */
export function parseInstanceCode(raw: string): { orgId: string | null; code: string } {
  const s = raw.trim()
  const alnum = s.replace(/[^A-Za-z0-9]/g, '')
  if (alnum.length <= CODE_LENGTH) return { orgId: null, code: alnum.toUpperCase() }

  // Walk back over the last 8 alphanumerics; everything before them is the org part. The org part
  // is validated as typed (not alnum-stripped): an org ID keeps its hyphens and has no spaces.
  let seen = 0
  let start = s.length
  while (start > 0 && seen < CODE_LENGTH) {
    start--
    if (/[A-Za-z0-9]/.test(s[start])) seen++
  }
  const code = s.slice(start).replace(/[^A-Za-z0-9]/g, '').toUpperCase()
  const orgPart = s.slice(0, start).replace(/[^A-Za-z0-9]+$/, '')
  const orgId = normalizeOrgId(orgPart)
  return orgId ? { orgId, code } : { orgId: null, code: '' }
}

/** `<orgId>-<code>` when the org ID is known, else the code unchanged. */
export function formatInstanceCode(code: string, orgId: string | null): string {
  return orgId ? `${orgId}-${code}` : code
}
